/**
 * "My activity" view — the caller's own command history for the
 * active site.
 *
 * Backed by the real audit trail (`GET /api/v1/sites/{site}/audit`,
 * plan §3.1) filtered server-side by `actor_sub` — the audit endpoint
 * already has a `paginate_at_ms(..., extra_filters=...)` hook, so
 * adding the filter was a small backend addition (`api/audit.py`)
 * rather than the client-side live-task-cache scan this screen used
 * to fall back to. Every command the caller issued (task dispatch,
 * door/lift commands, robot commission, etc.) shows here, not just
 * tasks — a strict superset of the old placeholder's scope.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertBanner } from '../../design-system/components/AlertBanner';
import { DataTable, type DataTableColumn } from '../../design-system/components/DataTable';
import { EmptyState } from '../../design-system/components/EmptyState';
import { useAuth } from '../../auth/useAuth';
import { useActiveSite } from '../../state/useActiveSite';
import { fetchAudit } from '../../api/v1-client';
import type { AuditEntry, SiteId } from '../../api/v1';
import { formatDateTime } from '../../utils/date-format';
import './ActivityScreen.css';
import { NoSiteNotice } from '../../shell/NoSiteNotice';
import { auditActionLabel } from '../Audit/audit-action-label';
import { AUDIT_OUTCOME_LABEL, auditTargetText } from '../Audit/audit-target';

const PAGE_SIZE = 50;

export function ActivityScreen() {
  const { user } = useAuth();
  const { site } = useActiveSite();

  const query = useInfiniteQuery({
    queryKey: ['v1', site?.id ?? '__none__', 'audit', 'current', '1.0', 'mine', user?.id ?? ''] as const,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      fetchAudit(site?.id as SiteId, pageParam, PAGE_SIZE, user?.id),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: site !== null && !!user,
  });

  if (!site) {
    return (
      <div className="nx-activity">
        <h1 className="nx-section-title">My activity</h1>
        <NoSiteNotice />
      </div>
    );
  }

  const entries: AuditEntry[] = (query.data?.pages ?? []).flatMap((p) => p.entries);

  // The Audit screen's own words for the same rows (review BC-F11 and
  // BB-B-4, 2026-10-31): since the account menu links here for every
  // role, the front desk read "door.declare_open", key=value dumps with
  // "[object Object]" in them, and raw outcome codes. A code with no
  // sentence still shows as a code, as on Audit.
  const columns: DataTableColumn<AuditEntry>[] = [
    { id: 'at', header: 'When', cell: (e) => formatDateTime(e.at_ms) },
    {
      id: 'action',
      header: 'Action',
      cell: (e) => {
        const label = auditActionLabel(e);
        return label ? <span title={e.action}>{label}</span> : <code>{e.action}</code>;
      },
    },
    { id: 'target', header: 'Target', cell: (e) => auditTargetText(e) },
    { id: 'outcome', header: 'Outcome', cell: (e) => AUDIT_OUTCOME_LABEL[e.outcome] ?? e.outcome },
  ];

  return (
    <div className="nx-activity">
      <h1 className="nx-section-title">My activity — {site.label}</h1>

      {query.isError && (
        <AlertBanner variant="error" title="Failed to load your activity">
          {query.error instanceof Error ? query.error.message : 'Unknown error'}
        </AlertBanner>
      )}

      {query.isLoading ? (
        <p>Loading…</p>
      ) : entries.length === 0 ? (
        <EmptyState
          title="No activity yet"
          description="Commands you issue today (task dispatch, door/lift control, etc.) will appear here."
        />
      ) : (
        <>
          <DataTable columns={columns} rows={entries} getRowId={(e) => e.audit_id} ariaLabel="My activity" />
          {query.hasNextPage && (
            <button
              type="button"
              className="nx-activity__load-more"
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
